import { useCallback, useEffect, useState } from 'react';
import { Card as MuiCard, Box, Typography, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { experienceCardStyles } from './ExperienceCard.styles';
import { Standards, StandardsProps, getMaxStandards } from '../../Standards/Standards';
import { StudentProgressCount } from '../../../core/StudentProgressCount/StudentProgressCount';
import { StudentProgress } from '../../StudentProgress/StudentProgress';
import { Badge } from '../../Badge/Badge';
import EndNowCheckIcon from './Images/end-now-check.svg';
import ReactivateBoltIcon from './Images/reactivate-bolt.svg';

function initializeMaxStandardsForAssignedOrScheduled(
  showSmallGroups: boolean,
  showReadingGroups: boolean,
  showTeacherNav: boolean,
  hasImage: boolean
): number {
  let adjustedMaxStandards = getMaxStandards();

  if (showSmallGroups) {
    adjustedMaxStandards -= 2;
  }
  if (showReadingGroups) {
    adjustedMaxStandards -= 2;
  }
  if (showTeacherNav) {
    adjustedMaxStandards -= 2;
  }
  if (hasImage) {
    adjustedMaxStandards -= 2;
  }

  return adjustedMaxStandards;
}

function initializeMaxStandardsForEnded(
  hasResponsesAverage: boolean,
  hasScore: boolean,
  hasImage: boolean
): number {
  let adjustedMaxStandards = getMaxStandards();

  if (hasResponsesAverage) {
    adjustedMaxStandards -= 2;
  }
  if (hasScore) {
    adjustedMaxStandards -= 2;
  }
  if (hasImage) {
    adjustedMaxStandards -= 2;
  }

  return adjustedMaxStandards;
}

export interface ExperienceCardProps {
  experienceId: number;
  name: string;
  description?: string;
  classNameText?: string;
  descriptionMaxLines?: number;
  imageUrl?: string;
  imageAlt?: string;
  studentsStarted: number;
  studentsInvited: number;
  startDate: Date;
  endDate: Date;
  isGuided?: boolean;
  hasGroups?: boolean;
  readingGroupId?: number;
  templateId?: number;
  gradeId?: number;
  standardSetId?: number;
  standardSetLabel?: string;
  standards?: StandardsProps['standards'];
  isStudent?: boolean;
  isAssignedExperience?: boolean;
  isScheduledExperience?: boolean;
  isEndedExperience?: boolean;
  canReactivate?: boolean;
  quizScore?: number;
  quizResponses?: number;
  canOpenExperience?: boolean;
  isEndNowConfirmOpen?: boolean;
  isReactivateConfirmOpen?: boolean;
  onExperienceClick: (params: { experienceId: number }) => void;
  onEndNowClick?: (params: { experienceId: number }) => void;
  onReactivateClick?: (params: { experienceId: number }) => void;
  onConfirmEndNow?: (params: { experienceId: number }) => void;
  onCancelEndNow?: (params: { experienceId: number }) => void;
  onConfirmReactivate?: (params: { experienceId: number }) => void;
  onCancelReactivate?: (params: { experienceId: number }) => void;
}

export const ExperienceCard = ({
  experienceId,
  name,
  description,
  classNameText,
  descriptionMaxLines = 2,
  imageUrl,
  imageAlt,
  studentsStarted,
  studentsInvited,
  startDate,
  endDate,
  isGuided,
  hasGroups,
  readingGroupId,
  templateId,
  gradeId,
  standardSetId,
  standardSetLabel,
  standards,
  isStudent,
  isAssignedExperience,
  isScheduledExperience,
  isEndedExperience,
  canReactivate,
  quizScore,
  quizResponses,
  canOpenExperience = true,
  isEndNowConfirmOpen,
  isReactivateConfirmOpen,
  onExperienceClick,
  onEndNowClick,
  onReactivateClick,
  onConfirmEndNow,
  onCancelEndNow,
  onConfirmReactivate,
  onCancelReactivate,
}: ExperienceCardProps) => {
  const theme = useTheme();
  const [hasStandardsContent, setHasStandardsContent] = useState(
    Array.isArray(standards) && standards.length > 0
  );
  const showSmallGroups = !!hasGroups;
  const showReadingGroups = readingGroupId != null && Number(readingGroupId) > 0;
  const showTeacherNav = !!isGuided;
  const hasBadges = !!isAssignedExperience && (showSmallGroups || showReadingGroups || showTeacherNav);
  const hasQuizResponses = typeof quizResponses === 'number' && !Number.isNaN(quizResponses);
  const hasQuizScore = typeof quizScore === 'number' && !Number.isNaN(quizScore);
  const showEndedScoreBadges = !!isEndedExperience && (hasQuizResponses || hasQuizScore);
  const formattedQuizScore = hasQuizScore ? `${quizScore}%` : '';
  const secondaryText = classNameText || description;
  const hasStandardsSource = !isStudent && (!!standards || !!standardSetId);
  const hasStandards = hasStandardsSource && hasStandardsContent;
  const hasVisibleMetadata = hasStandards || hasBadges || showEndedScoreBadges;
  // Keep service-backed standards mounted in a hidden row so they can load before the row is revealed.
  const shouldMountMetadata = hasStandardsSource || hasBadges || showEndedScoreBadges;
  const maxStandards = isEndedExperience
    ? initializeMaxStandardsForEnded(hasQuizResponses, hasQuizScore, !!imageUrl)
    : initializeMaxStandardsForAssignedOrScheduled(
      showSmallGroups,
      showReadingGroups,
      showTeacherNav,
      !!imageUrl
    );
  const progressPercentage = studentsInvited > 0
    ? Math.round((studentsStarted / studentsInvited) * 100)
    : 0;
  const hasEndNowHandler = typeof onEndNowClick === 'function';
  const hasReactivateHandler = typeof onReactivateClick === 'function';
  const showEndNowLink = Boolean(isAssignedExperience && hasEndNowHandler);
  const showReactivateLink = Boolean(isEndedExperience && canReactivate && hasReactivateHandler);

  useEffect(() => {
    setHasStandardsContent(Array.isArray(standards) && standards.length > 0);
  }, [standards, templateId, standardSetId, gradeId, isStudent]);

  const handleStandardsContentChange = useCallback((hasContent: boolean) => {
    setHasStandardsContent(hasContent);
  }, []);

  const handleCardClick = () => {
    if (!canOpenExperience) {
      return;
    }
    onExperienceClick({ experienceId });
  };

  const handleTitleKeyDown = (event: React.KeyboardEvent) => {
    if (!canOpenExperience) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onExperienceClick({ experienceId });
    }
  };

  const handleEndNowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEndNowClick?.({ experienceId });
  };

  const handleReactivateClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onReactivateClick?.({ experienceId });
  };

  const handleConfirmEndNowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onConfirmEndNow?.({ experienceId });
  };

  const handleCancelEndNowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onCancelEndNow?.({ experienceId });
  };

  const handleConfirmReactivateClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onConfirmReactivate?.({ experienceId });
  };

  const handleCancelReactivateClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onCancelReactivate?.({ experienceId });
  };

  return (
    <Box
      className="experience-card-frame"
      sx={experienceCardStyles.cardFrame(theme, !!isEndedExperience, canOpenExperience)}
    >
      <MuiCard
        sx={experienceCardStyles.card(theme, canOpenExperience)}
      >
      <Box sx={experienceCardStyles.topRow()}>
        <Box sx={experienceCardStyles.topActionsContainer(theme)}>
          {showEndNowLink && (
            <Link
              component="button"
              aria-label="End Now"
              className="end-now-link"
              sx={experienceCardStyles.endNowLink(theme)}
              onClick={handleEndNowClick}
            >
              <Box
                component="img"
                src={EndNowCheckIcon}
                alt="End Now checkmark"
                sx={experienceCardStyles.endNowIcon()}
              />
              End Now
            </Link>
          )}
          {showReactivateLink && (
            <Link
              component="button"
              aria-label="Reactivate"
              className="reactivate-link"
              sx={experienceCardStyles.endNowLink(theme)}
              onClick={handleReactivateClick}
            >
              <Box
                component="img"
                src={ReactivateBoltIcon}
                alt="Reactivate bolt"
                sx={experienceCardStyles.endNowIcon()}
              />
              Reactivate
            </Link>
          )}
          <Box sx={experienceCardStyles.studentProgressCountContainer()}>
            <StudentProgressCount
              progressedStudents={studentsStarted}
              invitedStudents={studentsInvited}
            />
          </Box>
        </Box>
        {imageUrl && (
          <Box sx={experienceCardStyles.imageContainer(theme)}>
            <Box
              component="img"
              src={imageUrl}
              alt={imageAlt || name}
              sx={experienceCardStyles.image()}
            />
          </Box>
        )}
        <Box sx={experienceCardStyles.contentContainer(theme)}>
          <Typography
            className="experience-card-title"
            sx={experienceCardStyles.title(theme, canOpenExperience)}
            onClick={canOpenExperience ? handleCardClick : undefined}
            onKeyDown={canOpenExperience ? handleTitleKeyDown : undefined}
            role={canOpenExperience ? 'link' : undefined}
            tabIndex={canOpenExperience ? 0 : undefined}
          >
            {name}
          </Typography>
          {secondaryText && (
            <Typography sx={experienceCardStyles.description(theme, descriptionMaxLines)}>
              {secondaryText}
            </Typography>
          )}
          {shouldMountMetadata && (
            <Box
              role="group"
              aria-label="Experience metadata"
              hidden={!hasVisibleMetadata}
              sx={experienceCardStyles.metadataRowContainer(theme)}
            >
              {hasStandardsSource && (
                <Box sx={experienceCardStyles.standardsContainer(theme)}>
                  <Standards
                    standardSetId={standardSetId}
                    standardSetLabel={standardSetLabel}
                    templateId={templateId}
                    gradeId={gradeId}
                    standards={standards}
                    maxStandards={maxStandards}
                    breakouts={!!standards}
                    showBreakouts={false}
                    onStandardsContentChange={handleStandardsContentChange}
                  />
                </Box>
              )}
              {(hasBadges || showEndedScoreBadges) && (
                <Box sx={experienceCardStyles.rightMetadataContainer(theme)}>
                  {hasBadges && (
                    <Box sx={experienceCardStyles.badgesRowContainer(theme)}>
                      {showSmallGroups && (
                        <Badge text="Small Groups" />
                      )}
                      {showReadingGroups && (
                        <Badge text="Reading Groups" />
                      )}
                      {showTeacherNav && (
                        <Badge text="Teacher Nav" />
                      )}
                    </Box>
                  )}
                  {showEndedScoreBadges && (
                    <Box sx={experienceCardStyles.endedScoresContainer(theme)}>
                      {hasQuizResponses && (
                        <Badge label="Responses" text={quizResponses!} size="compact" />
                      )}
                      {hasQuizScore && (
                        <Badge label="Quiz Score" text={formattedQuizScore} size="compact" />
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={experienceCardStyles.studentProgressContainer(theme)}>
        <StudentProgress
          progressPercentage={progressPercentage}
          startDate={startDate}
          endDate={endDate}
          isEndedExperience={!!isEndedExperience}
          isScheduledExperience={isScheduledExperience}
        />
      </Box>
      {isEndNowConfirmOpen && (
        <Box sx={experienceCardStyles.endNowConfirmOverlay()}>
          <Box sx={experienceCardStyles.endNowConfirmPanel()}>
            <Typography sx={experienceCardStyles.endNowConfirmTitle()}>
              End this experience for you and your students?
            </Typography>
            <Box sx={experienceCardStyles.endNowConfirmButtons()}>
              <button
                type="button"
                className="btn btn-sm btn-default"
                data-qa-role="btn#endExperience"
                onClick={handleConfirmEndNowClick}
              >
                Yes
              </button>
              <button
                type="button"
                className="btn btn-sm btn-plain"
                data-qa-role="btn#cancelEndExperience"
                style={{ color: '#FFFFFF' }}
                onClick={handleCancelEndNowClick}
              >
                No
              </button>
            </Box>
          </Box>
        </Box>
      )}
      {isReactivateConfirmOpen && (
        <Box sx={experienceCardStyles.endNowConfirmOverlay()}>
          <Box sx={experienceCardStyles.endNowConfirmPanel()}>
            <Typography sx={experienceCardStyles.reactivateConfirmTitle()}>
              Reactivate this experience to allow students to continue. It will be reactivated for 24 hours. To extend this timeframe, you can edit the End Time in the experience dashboard. Reactivate now?
            </Typography>
            <Box sx={experienceCardStyles.endNowConfirmButtons()}>
              <button
                type="button"
                className="btn btn-sm btn-default"
                data-qa-role="btn#reactivateExperience"
                onClick={handleConfirmReactivateClick}
              >
                Yes
              </button>
              <button
                type="button"
                className="btn btn-sm btn-plain"
                data-qa-role="btn#cancelReactivateExperience"
                style={{ color: '#FFFFFF' }}
                onClick={handleCancelReactivateClick}
              >
                No
              </button>
            </Box>
          </Box>
        </Box>
      )}
      </MuiCard>
    </Box>
  );
};

export default ExperienceCard;
